import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';
import financeClientsService, {
  ClientProfitability,
  ClientTeamMember,
} from '@/services/financeClients';
import { FetchApiIds } from '@/types';
import FinanceXClientsComponent from './FinanceXClients.component';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

function lastDayOfMonth(year: number, month: number): string {
  return new Date(year, month + 1, 0).toISOString().split('T')[0];
}

/** Cap end date at today so cost calculations don't exceed the period with actual revenue data */
function capAtToday(endDate: string): string {
  const today = new Date().toISOString().split('T')[0];
  return endDate > today ? today : endDate;
}

function getYearRange(year: number, cap = false): { startDate: string; endDate: string } {
  const endDate = `${year}-12-31`;
  return { startDate: `${year}-01-01`, endDate: cap ? capAtToday(endDate) : endDate };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const endDate = lastDayOfMonth(target.getFullYear(), target.getMonth());
  return { startDate: target.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const endDate = lastDayOfMonth(now.getFullYear(), q * 3 + 2);
  return { startDate: start.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

const FinanceXClientsContainer = function (): JSX.Element {
  const currentYear = new Date().getFullYear();
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const orgId = getActiveOrgId();
  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: 'this_year', ...getYearRange(currentYear, true) });
  const [editingMember, setEditingMember] = useState<
    (ClientTeamMember & { teamName: string }) | null
  >(null);

  const handlePresetChange = useCallback((preset: DatePreset) => {
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(currentYear, true) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(currentYear - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, [currentYear]);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate: capAtToday(endDate) });
  }, []);

  const { data: clients, isLoading } = useApiFetch<ClientProfitability[], void>(
    [
      FetchApiIds.FX_DASHBOARD_SUMMARY,
      'client-profitability',
      orgId,
      dateFilter.startDate,
      dateFilter.endDate,
    ],
    async () => {
      const result = await financeClientsService.getClientProfitability({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      });
      return { data: result } as any;
    },
    { retry: false }
  );

  // Revenue that reaches no client — surfaced so an unmapped vendor can't
  // silently make a client look unprofitable, as it did three times.
  const { data: unattributed } = useSeoFetch<{
    total: number;
    clients: Array<{ clientName: string; amount: number; invoices: number }>;
  }>(
    [FetchApiIds.FX_DASHBOARD_SUMMARY, 'unattributed-revenue', orgId, dateFilter.startDate, dateFilter.endDate],
    () =>
      financeClientsService.getUnattributedRevenue({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      }),
    { retry: false }
  );

  const clientList: ClientProfitability[] = useMemo(
    () => (clients as any)?.data ?? (clients as ClientProfitability[]) ?? [],
    [clients]
  );

  const totals = useMemo(() => {
    let revenue = 0;
    let cost = 0;
    clientList.forEach((c) => {
      revenue += c.revenue;
      cost += c.allocatedCost;
    });
    const profit = revenue - cost;
    const margin = revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : 0;
    return { revenue, cost, profit, margin };
  }, [clientList]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(FetchApiIds.FX_DASHBOARD_SUMMARY);
  }, [queryClient]);

  const handleUpdateRate = useCallback(
    async (
      memberId: string,
      monthlyRate: number | null,
      clientRate: number | null,
      clientRateType: 'hourly' | 'monthly',
      role: string | null,
      allocationPct: number,
      memberStartDate: string | null,
      memberEndDate: string | null
    ) => {
      try {
        await financeClientsService.updateMemberRate(memberId, {
          monthlyRate,
          clientRate,
          clientRateType,
          role,
          allocationPct,
          startDate: memberStartDate,
          endDate: memberEndDate,
        });
        invalidate();
        setEditingMember(null);
      } catch {
        toast.showErrorFeedback('Failed to update rate.');
      }
    },
    [invalidate, toast]
  );

  const handleCreateClient = useCallback(
    async (name: string) => {
      try {
        await financeClientsService.createClient(name);
        invalidate();
        toast.showSuccessFeedback(`Client "${name}" created.`);
      } catch (err: any) {
        console.error('createClient failed:', err);
        const detail = err?.message || err?.details || 'Unknown error';
        toast.showErrorFeedback(`Failed to create client: ${detail}`);
        throw err;
      }
    },
    [invalidate, toast]
  );

  return (
    <FinanceXClientsComponent
      clients={clientList}
      unattributed={unattributed ?? { total: 0, clients: [] }}
      isLoading={isLoading}
      totals={totals}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
      editingMember={editingMember}
      onEditMember={(member, teamName) => setEditingMember({ ...member, teamName })}
      onCloseEditMember={() => setEditingMember(null)}
      onUpdateRate={handleUpdateRate}
      onRemoveMember={async (memberId: string) => {
        try {
          // Try soft-delete first, fall back to hard delete
          const { error: updateErr } = await supabase
            .from('team_members')
            .update({ end_date: new Date().toISOString().split('T')[0] })
            .eq('id', memberId);
          if (updateErr) {
            // Hard delete as fallback
            const { error: deleteErr } = await supabase.from('team_members').delete().eq('id', memberId);
            if (deleteErr) throw deleteErr;
          }
          invalidate();
          setEditingMember(null);
        } catch {
          toast.showErrorFeedback('Failed to remove team member.');
        }
      }}
      onCreateClient={handleCreateClient}
      onAddMember={async (teamId: string, profileId: string) => {
        try {
          const { error } = await supabase
            .from('team_members')
            .insert({ team_id: teamId, profile_id: profileId });
          if (error) throw error;
          invalidate();
        } catch {
          toast.showErrorFeedback('Failed to add team member.');
        }
      }}
    />
  );
};

export default FinanceXClientsContainer;
